import React, { useCallback, useEffect, useRef, useState } from 'react';

import { storageDomain } from 'src/constants';

import { ErrorBoundary } from '../Errors/ErrorBoundary';
import { ReviewNotFound } from '../Errors/ReviewNotFound';
import Preloader from '../UI/Preloader';
import { Tooltips } from './Tooltips';
import {
    getFrameIndexFromImagePath,
    getFrameIndexWithOneBoundingFrame,
    getFrameIndexWithTwoBoundingFrames,
    getNormalizedFrameIndex,
} from 'src/components/ReviewViewer/helpers';
import { useWindowSizes } from 'src/helpers/hooks/useWindowSizes';
import { OnAddTooltipCordType } from 'src/components/ReviewViewer/Tooltips/types';
import { GoToFrameType } from 'src/components/ReviewViewer/types';

import styles from './ReviewViewer.module.scss';
import TouchableComponent, { IPos } from '../TouchableComponent';

interface IProps {
    frames: string[][];
    reverseMove?: boolean;
    cycledMove?: boolean;
}

const pixelRatio = window.devicePixelRatio || 1;

const ReviewViewer: React.FC<IProps> = (props: IProps) => {
    const { frames, reverseMove = false, cycledMove = true } = props;
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const ctx = useRef<CanvasRenderingContext2D>();
    const framesIndex = useRef<number>(0);
    const framesCount = useRef<number>(frames[framesIndex.current]?.length);

    const framesToLoad = useRef<string[][]>();

    const startedFrameIndex = Math.round((frames[framesIndex.current]?.length - 1) / 2);
    const [currentFrameIndex, setCurrentFrameIndex] = useState<number>(startedFrameIndex);

    const [isLoading, setLoading] = useState(true);
    const currentAbsoluteIndex = useRef<number>(0);
    const prevAbsoluteIndex = useRef<number>(0);

    const fullListLoaded = useRef<boolean>(false);
    const timer = useRef<any>();
    const IMGContainerRef = useRef<HTMLDivElement>(null);
    const imagesRef = useRef<HTMLImageElement[][]>([]);
    const aspectRef = useRef(1);

    const moveDirection = useRef<number>(reverseMove ? -1 : 1);
    const fadeValue = useRef<number>(0);

    const index = useRef<number>(startedFrameIndex);
    const pressedIndex = useRef<number>(0);
    const rowIndex = useRef<number>(0);
    const pressedRowIndex = useRef<number>(0);

    const pressed = useRef<boolean>(false);
    const pressMousePos = useRef<{ x: number; y: number }>({ x: Infinity, y: Infinity });

    const boundingFrameIndexes = useRef<[number] | [number, number] | null>(null);

    useWindowSizes(() => {
        if (canvasRef.current && IMGContainerRef.current) {
            let imgHeight = imagesRef.current[0]?.[0].height;
            let imgWidth = imagesRef.current[0]?.[0].width;
            let width: number = imgWidth;
            let height: number = imgHeight;

            if (imgWidth > window.innerWidth || imgHeight > window.innerHeight) {
                const heightByMaxWidth = window.innerWidth * aspectRef.current;
                if (heightByMaxWidth > window.innerHeight) {
                    height = window.innerHeight;
                    width = height / aspectRef.current;
                } else {
                    width = window.innerWidth;
                    height = heightByMaxWidth;
                }
            }

            canvasRef.current.width = width * pixelRatio;
            canvasRef.current.height = height * pixelRatio;
            canvasRef.current.style.width = `${width}px`;
            canvasRef.current.style.height = `${height}px`;

            IMGContainerRef.current.style.width = `${width}px`;
            IMGContainerRef.current.style.height = `${height}px`;
        }
    });

    const onTouchStart = useCallback((pos: IPos) => {
        pressMousePos.current.x = pos.x;
        pressMousePos.current.y = pos.y;
        pressedIndex.current = index.current;
        pressedRowIndex.current = rowIndex.current;
        pressed.current = true;
    }, []);

    const onTouchEnd = useCallback(() => {
        pressed.current = false;
        prevAbsoluteIndex.current = currentAbsoluteIndex.current;
    }, []);

    const onTouchMove = useCallback(
        (pos: IPos) => {
            if (!pressed.current) return;

            const startedFrameIndex = pressedIndex.current;
            let newAbsoluteFrameIndex =
                prevAbsoluteIndex.current +
                Math.round((pressMousePos.current.x - pos.x) / 10) * moveDirection.current;
            newAbsoluteFrameIndex -= prevAbsoluteIndex.current === 0 ? startedFrameIndex : 0;

            let newFrameIndex =
                startedFrameIndex -
                Math.round((pressMousePos.current.x - pos.x) / 10) * moveDirection.current;

            if (newFrameIndex !== index.current) {
                const maxFrameIndex = framesCount.current - 1;

                let normalizedNewFrameIndex = getNormalizedFrameIndex(
                    newAbsoluteFrameIndex,
                    framesCount.current,
                    cycledMove,
                );

                let resultFrameIndex: number;
                const boundingFrames = boundingFrameIndexes.current;

                if (boundingFrames?.length === 1) {
                    resultFrameIndex = getFrameIndexWithOneBoundingFrame(
                        startedFrameIndex,
                        boundingFrames[0],
                        newFrameIndex,
                        framesCount.current,
                        maxFrameIndex,
                    );
                } else if (boundingFrames?.length === 2) {
                    resultFrameIndex = getFrameIndexWithTwoBoundingFrames(
                        startedFrameIndex,
                        boundingFrames,
                        newFrameIndex,
                        framesCount.current,
                        maxFrameIndex,
                    );
                } else resultFrameIndex = normalizedNewFrameIndex;

                index.current = resultFrameIndex;
                setCurrentFrameIndex(resultFrameIndex);
            }

            if (!boundingFrameIndexes.current?.length) {
                const startedRowIndex = pressedRowIndex.current;
                const newRowIndex =
                    startedRowIndex -
                    Math.round((pressMousePos.current.y - pos.y) / 10) * moveDirection.current;

                const maxRowIndex = Math.max(imagesRef.current.length - 1, 0);

                if (newRowIndex > 0 && newRowIndex <= maxRowIndex) rowIndex.current = newRowIndex;
            }
            currentAbsoluteIndex.current = newAbsoluteFrameIndex;
        },
        [framesCount],
    );

    const goToFrame = useCallback<GoToFrameType>(
        (frameIndex, options) => {
            const { animationTime = 0, onReachFrame = () => {} } = options || {};

            const framesDifference = frameIndex - index.current;
            if (framesDifference === 0) {
                onReachFrame();
                return;
            }

            if (animationTime) {
                const interval = setInterval(() => {
                    const newFrameIndex = index.current + (framesDifference > 0 ? 1 : -1);

                    index.current = newFrameIndex;
                    prevAbsoluteIndex.current = currentAbsoluteIndex.current;
                    currentAbsoluteIndex.current += framesDifference > 0 ? 1 : -1;
                    setCurrentFrameIndex(newFrameIndex);

                    if (newFrameIndex === frameIndex) {
                        clearInterval(interval);
                        onReachFrame();
                    }
                }, 0);
            } else {
                index.current = frameIndex;
                prevAbsoluteIndex.current = frameIndex - 1;
                currentAbsoluteIndex.current = frameIndex;
                onReachFrame();
            }
        },
        [setCurrentFrameIndex],
    );

    const startRender = useCallback(() => {
        if (ctx.current && canvasRef.current) {
            cancelAnimationFrame(timer.current);
            const render = () => {
                const width = canvasRef.current?.width || 0;
                const height = canvasRef.current?.height || 0;

                ctx.current?.clearRect(
                    0,
                    0,
                    canvasRef.current?.width || 0,
                    canvasRef.current?.height || 0,
                );

                if (ctx.current) {
                    ctx.current?.drawImage(
                        imagesRef.current[rowIndex.current][index.current],
                        0,
                        0,
                        width,
                        height,
                    );
                    fadeValue.current = 0;
                }

                timer.current = requestAnimationFrame(render);
            };
            render();
        }
    }, [framesCount]);

    const loadImages = useCallback(
        async (_frames: string[][]) => {
            const sortedFrames = _frames.map(list =>
                list
                    .map(frame => frame)
                    .sort(
                        (imagePath1, imagePath2) =>
                            getFrameIndexFromImagePath(imagePath1) -
                            getFrameIndexFromImagePath(imagePath2),
                    ),
            );

            const countToLazyLoad = sortedFrames.reduce(
                (prev, list) => list.filter((_, i) => i % 2 === 1).length + prev,
                0,
            );

            let countLoaded = 0;
            const onLazyLoad = () => {
                countLoaded += 1;
                if (countLoaded >= countToLazyLoad) {
                    fullListLoaded.current = true;
                    console.log('FullFramesList loaded');
                }
            };

            imagesRef.current = await Promise.all(
                sortedFrames.map(list =>
                    Promise.all(
                        list.map<Promise<HTMLImageElement>>(
                            (item, i) =>
                                new Promise(async res => {
                                    const img = new Image();
                                    img.crossOrigin = 'Anonymous';

                                    if (i % 2 === 1) {
                                        img.alt = `${storageDomain}${item}`;
                                        res(img);
                                    } else {
                                        const imgBlob = await fetch(`${storageDomain}${item}`).then(
                                            response => response.blob(),
                                        );
                                        img.src = URL.createObjectURL(imgBlob);

                                        res(img);
                                    }
                                }),
                        ),
                    ),
                ),
            );

            imagesRef.current.forEach(frames => {
                frames.forEach((img, index) => {
                    if (index % 2 === 1) {
                        img.onload = onLazyLoad;
                        img.src = img.alt;
                    }
                });
            });

            //height and width does not have time to load, artificially slowing down the process
            await new Promise(res =>
                setTimeout(() => {
                    const height = imagesRef.current[0][0].height;
                    const width = imagesRef.current[0][0].width;
                    aspectRef.current = height / width;
                    res(1);
                }, 500),
            );

            await new Promise(res => setTimeout(() => res(1), 500));

            startRender();
        },
        [frames],
    );

    const onAddNewTooltipCord: OnAddTooltipCordType = coords => {
        if (coords.size === 1) {
            boundingFrameIndexes.current = [coords.keys().next().value];
        } else if (coords.size >= 2) {
            boundingFrameIndexes.current = [
                coords.keys().next().value,
                Array.from(coords)[coords.size - 1][0],
            ];
        }
    };

    useEffect(() => {
        if (canvasRef.current && !ctx.current) {
            ctx.current = canvasRef.current.getContext('2d') as CanvasRenderingContext2D;
            ctx.current.scale(pixelRatio, pixelRatio);
        }
    }, [canvasRef.current]);

    useEffect(() => {
        if (frames?.length) {
            if (!framesToLoad.current) {
                framesToLoad.current = frames;
            } else if (
                framesToLoad.current?.every((list, i) =>
                    list.every((item, j) => (frames[i] || [])[j] === item),
                )
            )
                return;
            setLoading(true);
            loadImages(frames).then(() => {
                setLoading(false);
            });
        }
    }, [frames]);

    return (
        <ErrorBoundary fallbackComponent={<ReviewNotFound />}>
            {isLoading ? <Preloader fullScreen /> : null}
            <div className={styles.imgFullScreenContainer}>
                <TouchableComponent
                    onTouchEnd={onTouchEnd}
                    onTouchStart={onTouchStart}
                    onTouchMove={onTouchMove}
                >
                    <div ref={IMGContainerRef} className={styles.imgCanvasContainer}>
                        <canvas ref={canvasRef} className={styles.imgCanvas} />
                        <Tooltips
                            canvasRef={canvasRef}
                            currentFrameIndex={currentFrameIndex}
                            framesMaxIndex={framesCount.current - 1}
                            setFrame={goToFrame}
                            onChangeMode={() => (boundingFrameIndexes.current = null)}
                            onAddNewTooltipCord={onAddNewTooltipCord}
                        />
                    </div>
                </TouchableComponent>
            </div>
        </ErrorBoundary>
    );
};

export { ReviewViewer };
